import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * Free-of-charge address autocomplete using Photon (OSM-based, no API key).
 * Renders the input the parent passes in via `children` is NOT used here —
 * the component owns the <input/>. The parent controls value via `value`/`onChange`.
 *
 * Props:
 *  - value          : string         current text value
 *  - onChange(str)  : function       called on every keystroke and on selection
 *  - placeholder    : string
 *  - icon           : ReactNode      left icon
 *  - testid         : string         data-testid for the input
 *  - dotColor       : "navy"|"orange"  marker color in suggestion list
 */
export default function AddressAutocomplete({
  value,
  onChange,
  placeholder,
  icon,
  testid,
  dotColor = "navy",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const justSelectedRef = useRef(false);
  const boxRef = useRef(null);

  // Debounced fetch
  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    const q = (value || "").trim();
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await api.autocomplete(q);
        if (cancelled) return;
        setSuggestions(data.suggestions || []);
        setOpen((data.suggestions || []).length > 0);
        setActive(-1);
      } catch {
        if (!cancelled) {
          setSuggestions([]);
          setOpen(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const pick = (s) => {
    justSelectedRef.current = true;
    onChange(s.label);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      pick(suggestions[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const dotHex = dotColor === "orange" ? "var(--orange)" : "var(--navy)";

  return (
    <div ref={boxRef} className="relative">
      <div
        className="flex items-center gap-3 px-4 py-3 rounded-2xl border bg-white"
        style={{ borderColor: "#DCE3ED" }}
      >
        <span style={{ color: "#7B8AA1", flexShrink: 0 }}>{icon}</span>
        <input
          data-testid={testid}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full bg-transparent outline-none text-[15px]"
          style={{ color: "var(--navy)" }}
        />
        {loading && (
          <span
            className="text-xs"
            style={{ color: "var(--mute)", flexShrink: 0 }}
            aria-hidden="true"
          >
            …
          </span>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-40 left-0 right-0 mt-2 rounded-2xl shadow-2xl overflow-hidden"
          style={{ background: "#FFFFFF", border: "1px solid #DCE3ED" }}
          data-testid={`${testid}-suggestions`}
        >
          {suggestions.map((s, i) => (
            <li key={`${s.label}-${i}`}>
              <button
                type="button"
                onMouseDown={(e) => {
                  // mousedown so input blur doesn't close before click fires
                  e.preventDefault();
                  pick(s);
                }}
                onMouseEnter={() => setActive(i)}
                className="w-full text-left px-4 py-2.5 flex items-start gap-2.5 text-[14px] transition-colors"
                style={{
                  background: active === i ? "var(--cream-2)" : "transparent",
                  color: "var(--navy)",
                  borderBottom:
                    i < suggestions.length - 1 ? "1px solid #EEF2F7" : "none",
                }}
                data-testid={`${testid}-suggestion-${i}`}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: dotHex,
                    marginTop: 7,
                    flexShrink: 0,
                  }}
                />
                <span className="flex-1 leading-snug">{s.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
